import type { EntityTypeId, GeneratorId } from "../content/types";
import { Bitmap, BitmapData, Point, Rectangle, Sprite } from "../runtime";
import {
  G_bossBar,
  G_bossBarBit,
  SPR_barrierBeam,
  SPR_barrierBlock,
  enemyHPBar,
  mDot,
} from "./assets";
import { STATIC_ENTITY_SHEETS, type SheetClass } from "./data/entitySheets";
import type { EnemyHitData } from "./combat";
import type { Barrier, Obstacle } from "./level";
import { Entity } from "./Entity";
import { EffectId, EntityId } from "./ids";
import type { EntityFactory } from "./entities/EntityFactory";

/** Score popup owed for a killed enemy. */
interface RewardDrop {
  xx: number;
  yy: number;
  points: number;
}

/** Pickup dropped where an enemy died; `id` selects the `Effect` sprite. */
interface ItemDrop {
  xx: number;
  yy: number;
  id: EffectId;
}

/** Request for an `Effect` at a point. */
interface EffectSpawn {
  id: EffectId;
  xx: number;
  yy: number;
}

/** An entity effect that also carries an angle or frame offset. */
interface ExplodeSpawn {
  id: EntityId;
  xx: number;
  yy: number;
  extra: number;
}

/** Sword-impact flash owed at a kill. */
interface StrikeSpawn {
  xx: number;
  yy: number;
}

/** Delayed blast hitbox, counted down by `Game.dangerZoneEvents`. */
interface DangerZone {
  xx: number;
  yy: number;
  hitRadius: number;
  damage: number;
  frameActive: number;
  frameCounter: number;
}

/** A destroyed Xenos generator, by its authored target id. */
interface GeneratorHit {
  xx: number;
  yy: number;
  id: GeneratorId;
}

/** Beam/block animation shared by every barrier in the level. */
interface BarrierAnim {
  beamFrame: number;
  blockFrame: number;
  frameCounter: number;
  beamRectV: Rectangle;
  beamRectH: Rectangle;
  blockRectV: Rectangle;
  blockRectH: Rectangle;
}

export class ObjectGenerator extends Sprite {
  allObjects: Entity[] = [];
  objectTotalOffset = 0;
  barriers!: Barrier[];
  rewardArray: RewardDrop[] = [];
  itemDropArray: ItemDrop[] = [];
  soundArray: number[] = [];
  generatorDamaged: GeneratorHit[] = [];
  explodeArray: ExplodeSpawn[] = [];
  strikeArray: StrikeSpawn[] = [];
  dangerZone: DangerZone[] = [];
  effectArray: EffectSpawn[] = [];
  private id = 0;
  freeze = 0;
  private readonly spriteArray: readonly (BitmapData | null)[];
  private readonly entityFactory?: EntityFactory;
  private readonly authoredSheets = new Map<SheetClass, BitmapData>();
  private readonly stageBitmapData: BitmapData;
  private readonly stageRect: Rectangle;
  private readonly objectSnapshots: EnemyHitData[] = [];
  private readonly nearbyObstacleBuckets: Obstacle[][] = [];
  private readonly objectDrawPos = new Point(0, 0);
  private readonly barrierDrawPos = new Point(0, 0);
  private readonly hpBarPos = new Point(0, 0);
  private readonly hpDotPos = new Point(0, 0);
  private readonly enemyHPBarRect = new Rectangle(0, 0, 41, 8);
  private readonly enemyHPDotRect = new Rectangle(0, 0, 3, 4);
  private readonly bossHPBarRect = new Rectangle(0, 0, 102, 13);
  private readonly bossHPDotRect = new Rectangle(0, 0, 7, 9);
  private eHPBar!: enemyHPBar;
  private hpDot!: mDot;
  private bossBar!: G_bossBar;
  private bossBarBit!: G_bossBarBit;
  private bBeamSpr!: SPR_barrierBeam;
  private bBlockSpr!: SPR_barrierBlock;
  private bData!: BarrierAnim;
  private readonly bgX: number;
  private readonly bgTotal: number;
  private readonly levelWidth: number;
  private readonly levelHeight: number;
  private alternator = false;

  constructor(
    id = 0,
    stageWidth = 0,
    stageHeight = 0,
    backgroundColumns = 0,
    backgroundRows = 0,
    entityFactory?: EntityFactory,
  ) {
    super();
    this.bgX = backgroundColumns;
    this.bgTotal = backgroundColumns * backgroundRows;
    this.levelWidth = backgroundColumns * 512;
    this.levelHeight = backgroundRows * 512;
    this.spriteArray = buildSheets();
    this.entityFactory = entityFactory;
    this.id = id;
    if (this.id === 0) {
      this.bData = {
        beamFrame: 0,
        blockFrame: 0,
        frameCounter: 0,
        beamRectV: new Rectangle(0, 0, 64, 64),
        beamRectH: new Rectangle(0, 0, 64, 64),
        blockRectV: new Rectangle(0, 0, 64, 64),
        blockRectH: new Rectangle(0, 0, 64, 64),
      };
      this.eHPBar = new enemyHPBar(0, 0);
      this.hpDot = new mDot(0, 0);
      this.bossBar = new G_bossBar(0, 0);
      this.bossBarBit = new G_bossBarBit(0, 0);
      this.bBeamSpr = new SPR_barrierBeam(0, 0);
      this.bBlockSpr = new SPR_barrierBlock(0, 0);
    }
    this.stageRect = new Rectangle(0, 0, stageWidth, stageHeight);
    this.stageBitmapData = new BitmapData(
      stageWidth,
      stageHeight,
      true,
      13816575,
    );
    this.addChild(new Bitmap(this.stageBitmapData));
  }

  /**
   * Advances and redraws every object. The returned snapshots line up with
   * `allObjects` by index, which is how `Game` hits enemies.
   */
  update(
    knightX: number,
    knightY: number,
    offsetX: number,
    offsetY: number,
    obsArray: Obstacle[][],
    barrierArray: Obstacle[],
    enemyAttacksOn: boolean,
  ): EnemyHitData[] {
    this.stageBitmapData.lock();
    this.stageBitmapData.fillRect(this.stageRect, 16777215);
    const objData = this.objectSnapshots;
    objData.length = 0;
    if (this.id === 0) {
      this.updateBarriers(offsetX, offsetY);
    }
    for (
      let objectIndex = 0;
      objectIndex < this.allObjects.length;
      objectIndex++
    ) {
      const currentObj = this.allObjects[objectIndex];
      let canDraw = true;
      if (this.id === 0) {
        this.updateEnemy(
          currentObj,
          knightX,
          knightY,
          obsArray,
          barrierArray,
          enemyAttacksOn,
        );
        if (currentObj.rollData.vanished) {
          canDraw = false;
        }
      } else {
        if (currentObj.id === EntityId.Missile) {
          currentObj.updateMissile(knightX, knightY);
        }
        currentObj.effectUpdate();
      }
      const xPos = currentObj.xPos;
      const yPos = currentObj.yPos;
      const destroyed = currentObj.updateAnim();
      const drawPos = this.objectDrawPos;
      drawPos.x = currentObj.finalPos.x + offsetX;
      drawPos.y = currentObj.finalPos.y + offsetY;
      const objectSheet = this.sheetFor(currentObj);
      if (canDraw) {
        this.stageBitmapData.copyPixels(
          objectSheet!,
          currentObj.tileRect,
          drawPos,
          null,
          null,
          true,
        );
      }
      if (this.id === 0) {
        if (currentObj.rollData.hpBarCD > 0) {
          this.drawHPBar(currentObj, drawPos.x, drawPos.y);
        }
        if (
          currentObj.isMine &&
          currentObj.rollData.slashState === 13 &&
          currentObj.rollData.counter === 0
        ) {
          this.detonateMine(currentObj);
        }
        objData.push({
          xx: xPos,
          yy: yPos,
          dead: currentObj.rollData.vanished ? true : currentObj.dead,
          hitRadius: currentObj.hitRadius,
        });
      }
      switch (destroyed) {
        case 1:
          if (currentObj.enemy && currentObj.rollData.secondary === false) {
            this.spawnSlash(xPos, yPos, knightX, knightY);
          }
          break;
        case 2:
          if (currentObj.enemy) {
            this.killEnemy(currentObj, xPos, yPos, knightX, knightY);
          } else if (currentObj.projectile) {
            if (currentObj.id !== EntityId.Missile) {
              this.generateEffect(
                EntityId.ShotAbsorb,
                currentObj.xPos,
                currentObj.yPos,
              );
            } else {
              this.generateEffect(
                EntityId.HugeExplosion,
                currentObj.xPos,
                currentObj.yPos,
              );
              this.soundArray.push(0);
            }
          }
          this.allObjects.splice(objectIndex, 1);
          if (this.id === 0) {
            objData.splice(objData.length - 1, 1);
          }
          objectIndex--;
          break;
        case 4:
          this.soundArray.push(0);
          break;
        case 5:
          this.soundArray.push(1);
          break;
        case 6:
          if (currentObj.warpsOnRoll) {
            this.warpBoss(currentObj, knightX, knightY);
          }
          break;
      }
    }
    this.stageBitmapData.unlock();
    return objData;
  }

  hitEnemy(enemyIndex = 0, hitDelay = 0, hitAngle = 0, damage = 0): number {
    hitAngle += (Math.ceil(Math.random() * 150) - 75) * 0.1;
    const hd: number = hitDelay + 1;
    return this.allObjects[enemyIndex].hit(hd, hitAngle, damage) | 0;
  }

  generateBoss(
    x: number,
    y: number,
    entity: EntityTypeId,
    variant?: string,
  ): void {
    const boss = this.factory().create(entity, x, y, {
      levelWidth: this.levelWidth,
      levelHeight: this.levelHeight,
      variant,
    });
    this.sheetFor(boss);
    this.allObjects.push(boss);
  }

  /** Spawns `count` enemies in expanding rings around `x`/`y`. */
  generateEnemies(
    x: number,
    y: number,
    entity: EntityTypeId,
    count: number,
    target?: GeneratorId,
  ): void {
    const factory = this.factory();
    let total = count;
    const rowTotal = Math.ceil(total / 12);
    const angleSpacing = 360 / (total / rowTotal);
    const definition = factory.definition(entity);
    if (definition.behavior === "generator") total = 1;
    let angleCurrent = 0;
    let dist = 20;
    let previousRow = 0;
    for (let enemyIndex = 0; enemyIndex < total; enemyIndex++) {
      this.alternator = !this.alternator;
      const rowCurrent = Math.floor(enemyIndex / 12);
      if (previousRow !== rowCurrent) {
        previousRow = rowCurrent;
        angleCurrent += angleSpacing * 0.5;
        dist += 10;
      }
      const rad = angleCurrent * (Math.PI / 180);
      let xOffset = dist * Math.cos(rad);
      let yOffset = dist * Math.sin(rad);
      if (total === 1) {
        xOffset = 0;
        yOffset = 0;
      }
      const enemy = factory.create(entity, x + xOffset, y + yOffset, {
        levelWidth: this.levelWidth,
        levelHeight: this.levelHeight,
        target,
      });
      this.sheetFor(enemy);
      enemy.rollData.alternate = this.alternator;
      this.allObjects.push(enemy);
      angleCurrent += angleSpacing;
    }
    if (definition.behavior === "mine" || definition.behavior === "generator") {
      this.objectTotalOffset += total;
    }
  }

  generateEffect(
    entityId: EntityId = EntityId.Spark,
    x = 0,
    y = 0,
    extraValue = 0,
  ): void {
    // moving explosion is a three-shard burst, the outer two fanned by +-17 degrees
    const effectTotal = entityId === EntityId.MovingExplosion ? 3 : 1;
    for (let effectIndex = 0; effectIndex < effectTotal; effectIndex++) {
      let extraValueFinal = extraValue;
      if (entityId === EntityId.MovingExplosion) {
        if (effectIndex === 0) {
          extraValueFinal -= 17;
        }
        if (effectIndex === 2) {
          extraValueFinal += 17;
        }
      }
      this.allObjects.push(new Entity(entityId, x, y, extraValueFinal));
    }
  }

  private factory(): EntityFactory {
    if (this.entityFactory === undefined) {
      throw new Error("authored enemy generator requires an EntityFactory");
    }
    return this.entityFactory;
  }

  private sheetFor(entity: Entity): BitmapData {
    const sheetClass = entity.spriteClass;
    if (sheetClass === null) return this.spriteArray[entity.id!]!;
    let sheet = this.authoredSheets.get(sheetClass);
    if (sheet === undefined) {
      sheet = new sheetClass();
      this.authoredSheets.set(sheetClass, sheet);
    }
    return sheet;
  }

  /** Cycles the shared beam/block frames, then draws and ages every barrier. */
  private updateBarriers(offsetX: number, offsetY: number): void {
    const bd = this.bData;
    ++bd.frameCounter;
    if (bd.frameCounter === 2) {
      ++bd.beamFrame;
      ++bd.blockFrame;
      bd.frameCounter = 0;
      if (bd.beamFrame === 12) {
        bd.beamFrame = 0;
      }
      if (bd.blockFrame === 16) {
        bd.blockFrame = 0;
      }
      bd.beamRectH.x = ((bd.beamFrame % 6) | 0) * 64;
      bd.beamRectH.y = ((bd.beamFrame / 6) | 0) * 64;
      bd.beamRectV.x = (((bd.beamFrame + 12) % 6) | 0) * 64;
      bd.beamRectV.y = (((bd.beamFrame + 12) / 6) | 0) * 64;
      bd.blockRectH.x = ((bd.blockFrame % 8) | 0) * 64;
      bd.blockRectH.y = ((bd.blockFrame / 8) | 0) * 64;
      bd.blockRectV.x = (((bd.blockFrame + 16) % 8) | 0) * 64;
      bd.blockRectV.y = (((bd.blockFrame + 16) / 8) | 0) * 64;
    }
    for (
      let barrierIndex = 0;
      barrierIndex < this.barriers.length;
      barrierIndex++
    ) {
      const barrier = this.barriers[barrierIndex];
      if (barrier.vanished === false) {
        // ids 0-2 are horizontal beams, 3-5 vertical
        const beamRectFinal = barrier.id <= 2 ? bd.beamRectH : bd.beamRectV;
        const beamPos = this.barrierDrawPos;
        beamPos.x = barrier.xx + offsetX;
        beamPos.y = barrier.yy + offsetY;
        this.stageBitmapData.copyPixels(
          this.bBeamSpr,
          beamRectFinal,
          beamPos,
          null,
          null,
          true,
        );
        // 2 and 5 are the mid-run segments, which have no end cap
        if (barrier.id !== 2 && barrier.id !== 5) {
          let blockRectFinal: Rectangle;
          if (barrier.id < 3) {
            beamPos.x += barrier.id === 0 ? -32 : 32;
            blockRectFinal = bd.blockRectH;
          } else {
            beamPos.y += barrier.id === 3 ? -32 : 32;
            blockRectFinal = bd.blockRectV;
          }
          this.stageBitmapData.copyPixels(
            this.bBlockSpr,
            blockRectFinal,
            beamPos,
            null,
            null,
            true,
          );
        }
      }
      if (barrier.isOn) {
        if (barrier.vanishCounter < 100) {
          barrier.vanishCounter += 3;
          if (barrier.vanishCounter >= barrier.vanishRate) {
            barrier.vanished = !barrier.vanished;
            barrier.vanishRate =
              barrier.vanishCounter + Math.ceil(100 / barrier.vanishCounter);
          }
          if (barrier.vanishCounter >= 100) {
            barrier.vanishCounter = 100;
            barrier.vanishRate = 98;
            barrier.vanished = false;
          }
        }
      } else if (barrier.vanishCounter > 0) {
        barrier.vanishCounter -= 3;
        if (barrier.vanishCounter <= barrier.vanishRate) {
          barrier.vanished = !barrier.vanished;
          barrier.vanishRate =
            barrier.vanishCounter + Math.ceil(100 / barrier.vanishCounter);
        }
        if (barrier.vanishCounter <= 0) {
          this.barriers.splice(barrierIndex, 1);
          barrierIndex--;
        }
      }
    }
  }

  /** Ticks one enemy and queues the effects its action code asks for. */
  private updateEnemy(
    currentObj: Entity,
    knightX: number,
    knightY: number,
    obsArray: Obstacle[][],
    barrierArray: Obstacle[],
    enemyAttacksOn: boolean,
  ): void {
    const obsFinal = this.nearbyObstacleBuckets;
    obsFinal.length = 0;
    const currentXSquare = Math.floor(currentObj.xPos / 512);
    const currentYSquare = Math.floor(currentObj.yPos / 512);
    obsFinal.push(obsArray[currentXSquare + currentYSquare * this.bgX]);
    const leftBlock = currentXSquare - 1 + currentYSquare * this.bgX;
    if (leftBlock >= 0) {
      obsFinal.push(obsArray[leftBlock]);
    }
    const rightBlock = currentXSquare + 1 + currentYSquare * this.bgX;
    if (rightBlock < this.bgTotal) {
      obsFinal.push(obsArray[rightBlock]);
    }
    const upperBlock = currentXSquare + (currentYSquare - 1) * this.bgX;
    if (upperBlock >= 0) {
      obsFinal.push(obsArray[upperBlock]);
    }
    const lowerBlock = currentXSquare + (currentYSquare + 1) * this.bgX;
    if (lowerBlock < this.bgTotal) {
      obsFinal.push(obsArray[lowerBlock]);
    }
    if (enemyAttacksOn === false) {
      currentObj.holdActions();
    }
    const enemyAction = currentObj.enemyUpdate(
      knightX,
      knightY,
      obsFinal,
      barrierArray,
    );
    switch (enemyAction) {
      case 1:
        // the heavier enemies warp in with the larger effect
        this.explodeArray.push({
          id: currentObj.usesLargeEffects
            ? EntityId.LargeWarp
            : EntityId.SmallWarp,
          xx: currentObj.xPos,
          yy: currentObj.yPos,
          extra: 0,
        });
        break;
      case 2:
        this.explodeArray.push({
          id: EntityId.EnemySwipeA,
          xx: currentObj.xPos - 3,
          yy: currentObj.yPos,
          extra: 0,
        });
        break;
      case 3:
        this.explodeArray.push({
          id: EntityId.EnemySwipeB,
          xx: currentObj.xPos + 3,
          yy: currentObj.yPos,
          extra: 0,
        });
        break;
      case 4:
        this.explodeArray.push({
          id: EntityId.EnemyESlashA,
          xx: currentObj.xPos - 8,
          yy: currentObj.yPos - 8,
          extra: 0,
        });
        break;
      case 5:
        this.explodeArray.push({
          id: EntityId.EnemyESlashB,
          xx: currentObj.xPos + 8,
          yy: currentObj.yPos - 8,
          extra: 0,
        });
        break;
      case 6:
        for (let laserIndex = 0; laserIndex < 18; laserIndex++) {
          this.explodeArray.push({
            id: EntityId.RotatingLaser,
            xx: currentObj.xPos + 3,
            yy: currentObj.yPos - 3,
            extra: laserIndex * 20,
          });
        }
        this.explodeArray.push({
          id: EntityId.LargeSwordCharge,
          xx: currentObj.xPos + 3,
          yy: currentObj.yPos - 3,
          extra: 0,
        });
        break;
      case 15:
        this.itemDropArray.push({
          xx: currentObj.xPos,
          yy: currentObj.yPos,
          id: EffectId.SmallHealthPickup,
        });
        break;
    }
    if (
      currentObj.rollData.slashState > 0 &&
      currentObj.rollData.slashState < 3 &&
      currentObj.rollData.counter === 0
    ) {
      if (!currentObj.usesLargeEffects) {
        this.explodeArray.push({
          id: EntityId.SwordCharge,
          xx: currentObj.xPos,
          yy: currentObj.yPos - 17,
          extra: 0,
        });
      } else {
        const slashXOffset = currentObj.flipped ? -25 : 25;
        this.explodeArray.push({
          id: EntityId.LargeSwordCharge,
          xx: currentObj.xPos + slashXOffset,
          yy: currentObj.yPos - 30,
          extra: 0,
        });
      }
    }
  }

  /** Draws the floating hp readout above an enemy, or the wider boss bar. */
  private drawHPBar(currentObj: Entity, drawX: number, drawY: number): void {
    --currentObj.rollData.hpBarCD;
    const barPos = this.hpBarPos;
    barPos.x = drawX;
    barPos.y = drawY;
    const hpPos = this.hpDotPos;
    const hp = Math.ceil(currentObj.rollData.hpCurrent * 0.1);
    if (!currentObj.isBoss) {
      barPos.x += currentObj.rollData.hpBarX - 21;
      barPos.y -= 4;
      this.stageBitmapData.copyPixels(
        this.eHPBar,
        this.enemyHPBarRect,
        barPos,
        null,
        null,
        true,
      );
      // hp dots remain relative to the shifted bar position because the original points aliased
      hpPos.x = barPos.x + 6;
      hpPos.y = barPos.y + 2;
      for (let h = 0; h < hp; h++) {
        this.stageBitmapData.copyPixels(
          this.hpDot,
          this.enemyHPDotRect,
          hpPos,
          null,
          null,
          true,
        );
        hpPos.x += 3;
      }
    } else {
      barPos.x += currentObj.rollData.hpBarX - 51;
      barPos.y += 4;
      this.stageBitmapData.copyPixels(
        this.bossBar,
        this.bossHPBarRect,
        barPos,
        null,
        null,
        true,
      );
      // boss bits retain the same aliased offset from the shifted bar position
      hpPos.x = barPos.x + 7;
      hpPos.y = barPos.y + 2;
      for (let hBoss = 0; hBoss < hp; hBoss++) {
        this.stageBitmapData.copyPixels(
          this.bossBarBit,
          this.bossHPDotRect,
          hpPos,
          null,
          null,
          true,
        );
        hpPos.x += 9;
      }
    }
  }

  /** Sets off a rolling mine the moment it touches any other live object. */
  private detonateMine(currentObj: Entity): void {
    for (
      let otherIndex = 0;
      otherIndex < this.allObjects.length;
      otherIndex++
    ) {
      if (currentObj.dead) {
        continue;
      }
      const other = this.allObjects[otherIndex];
      if (other.dead || other.isMine) {
        continue;
      }
      const dxRoll = other.xPos - currentObj.xPos;
      const dyRoll = other.yPos - currentObj.yPos;
      const mineDist = Math.sqrt(dxRoll * dxRoll + dyRoll * dyRoll);
      if (mineDist < currentObj.hitRadius + other.hitRadius) {
        currentObj.dead = true;
        this.explodeArray.push({
          id: EntityId.LargeSwordCharge,
          xx: currentObj.xPos,
          yy: currentObj.yPos,
          extra: 0,
        });
      }
    }
  }

  /** Queues the slash effect and impact flash for a hit that did not kill. */
  private spawnSlash(
    xPos: number,
    yPos: number,
    knightX: number,
    knightY: number,
  ): void {
    const slashAngle =
      Math.atan2(yPos - knightY, xPos - knightX) * (180 / Math.PI);
    const dx = knightX - xPos;
    const dy = knightY - yPos;
    const slashDist = Math.sqrt(dx * dx + dy * dy);
    const rad = (slashAngle + 180) * (Math.PI / 180);
    const slX = slashDist * 0.15 * Math.cos(rad);
    const slY = slashDist * 0.15 * Math.sin(rad);
    this.strikeArray.push({ xx: xPos - slX, yy: yPos - slY });
    this.explodeArray.push({
      id: EntityId.Spark,
      xx: xPos + slX,
      yy: yPos + slY,
      extra: 1,
    });
  }

  /** Pays out score, drops and debris for an enemy that just died. */
  private killEnemy(
    currentObj: Entity,
    xPos: number,
    yPos: number,
    knightX: number,
    knightY: number,
  ): void {
    this.rewardArray.push({
      xx: currentObj.xPos,
      yy: currentObj.yPos,
      points: currentObj.rollData.rewardPoints,
    });
    if (currentObj.dropsItems) {
      const randomDrop = Math.floor(Math.random() * 100);
      if (randomDrop < currentObj.rollData.dropChanceXenos) {
        this.itemDropArray.push({
          xx: currentObj.xPos,
          yy: currentObj.yPos,
          id: EffectId.MediumXenosPickup,
        });
      } else if (randomDrop < currentObj.rollData.dropChanceHP) {
        this.itemDropArray.push({
          xx: currentObj.xPos,
          yy: currentObj.yPos,
          id: EffectId.SmallHealthPickup,
        });
      }
      const explodeAngle =
        Math.atan2(yPos - knightY, xPos - knightX) * (180 / Math.PI);
      this.explodeArray.push({
        id: EntityId.MovingExplosion,
        xx: xPos,
        yy: yPos,
        extra: explodeAngle,
      });
      // the heavier enemies leave the bigger fireball
      this.explodeArray.push({
        id: currentObj.usesLargeEffects
          ? EntityId.LargeExplosion
          : EntityId.Explosion,
        xx: xPos,
        yy: yPos,
        extra: 0,
      });
    } else if (currentObj.isMine || currentObj.isGenerator) {
      --this.objectTotalOffset;
      if (currentObj.isMine) {
        this.explodeArray.push({
          id: EntityId.HugeExplosion,
          xx: xPos,
          yy: yPos,
          extra: 0,
        });
        this.dangerZone.push({
          xx: xPos,
          yy: yPos,
          hitRadius: 36,
          damage: 35,
          frameActive: 2,
          frameCounter: 0,
        });
      }
      if (currentObj.isGenerator) {
        this.explodeArray.push({
          id: EntityId.LargeExplosion,
          xx: xPos,
          yy: yPos,
          extra: 0,
        });
        this.generatorDamaged.push({
          xx: xPos,
          yy: yPos,
          id: currentObj.rollData.targetID!,
        });
      }
    }
  }

  /** Blinks the final boss out and back in on a ring around the player. */
  private warpBoss(currentObj: Entity, knightX: number, knightY: number): void {
    const fBossAngle = Math.round(Math.random() * 360);
    const fBossRad = fBossAngle * (Math.PI / 180);
    const fBossX = knightX + 145 * Math.cos(fBossRad);
    const fBossY = knightY + 145 * Math.sin(fBossRad);
    this.effectArray.push({
      id: EffectId.LargePickupBurst,
      xx: currentObj.xPos,
      yy: currentObj.yPos,
    });
    this.explodeArray.push({
      id: EntityId.LargeWarp,
      xx: fBossX,
      yy: fBossY,
      extra: 0,
    });
    currentObj.teleport(fBossX, fBossY);
    this.soundArray.push(2);
  }
}

/** One sheet instance per class, shared by every id that draws from it. */
function buildSheets(): readonly (BitmapData | null)[] {
  const built = new Map<SheetClass, BitmapData>();
  return Array.from({ length: EntityId.LaserShot + 1 }, (_, objectId) => {
    const id = objectId as EntityId;
    if (id < EntityId.Spark) return null;
    const sheetClass = STATIC_ENTITY_SHEETS[id];
    if (sheetClass == null) {
      return null;
    }
    let sheet = built.get(sheetClass);
    if (sheet === undefined) {
      sheet = new sheetClass();
      built.set(sheetClass, sheet);
    }
    return sheet;
  });
}
